import produce from 'utils/produce';
import { getPageByOptionId, getTemplatePage, PAGE_OPTION } from 'editor-utils/template';
import { generatePage } from '../../generator/utils';

import BlankPagePanel from './blankPage/blankPagePanel';
import FamilySheetPanel from './familySheet/familySheetPanel';
import FamilyTreePanel from './familyTree/familyTreePanel';
import PersonPanel from './person/personPanel';
import PersonalTimelinePanel from './personalTimeline/personalTimelinePanel';
import PhotoPanel from './photo/photoPanel';
import RecordPanel from './record/recordPanel';

const OPTIONS = {
  //
  // Add BLANK page
  // When template defines blank page, no dialog required. Otherwise, choose one page.
  // Returns one blank page.
  //
  blank: {
    title: 'Add Blank Page',
    text:
      'A blank canvas that you can use to add any text or photos to your project. This is the page you will want to select if you want to add a photo from Ancestry.',
    onSelect: ({ addPages, card, template, openPanel }) => {
      const blankPage = getTemplatePage(template, 'blank');
      if (blankPage) {
        addPages([generatePage(blankPage)]);
      } else {
        openPanel({
          props: { onAddPages: addPages },
          renderPanel: BlankPagePanel,
          title: card.title,
        });
      }
    },
    isDisabled: () => false,
  },

  //
  // Add Title
  // Returns title and dedication page.
  //
  title: {
    title: 'Add Title',
    text:
      'Add a title page to your book. This will be placed before the Table of Contents and will become the first page people see when the book is opened. This will also include a Dedication Page on the reverse of this page that can be customized.',
    onSelect: ({ addPages, template }) => {
      // TODO is this really good approach? Or I have to select a person?
      const titlePage = getTemplatePage(template, 'title');
      let dedicationPage = getTemplatePage(template, 'dedication');

      if (dedicationPage == null) {
        dedicationPage = getTemplatePage(template, 'blank');
      }

      const titlePageData = generatePage(titlePage);
      const dedicationPageData = generatePage(dedicationPage);
      dedicationPageData.placeholders = [...dedicationPageData.placeholders].map(placeholder => {
        if (placeholder.type === 'text') {
          const templatePlaceholder = dedicationPage.placeholders.find(
            p => p.id === placeholder.id,
          );
          if (templatePlaceholder?.label) {
            return {
              ...placeholder,
              data: {
                value: templatePlaceholder.label,
              },
            };
          }
        }
        return placeholder;
      });

      addPages([titlePageData, dedicationPageData], { index: 1 });
    },
    isDisabled: ({ template }) => {
      const titlePage = getTemplatePage(template, 'title');
      let dedicationPage = getTemplatePage(template, 'dedication');

      if (dedicationPage == null) {
        dedicationPage = getTemplatePage(template, 'blank');
      }

      if (!titlePage || !dedicationPage) {
        return true;
      }

      return false;
    },
  },

  //
  // Add Table of Contents
  // No dialog required.
  // Returns one TOC page and then editor automatically update TOC.
  //
  'table-of-contents': {
    title: 'Add Table of Contents',
    text:
      'Recreate your Table of Contents if you did not add one already.  This will replace your current Table of Contents if you already have one so you will lose any manual edits you made.',
    onSelect: ({ addPages, template }) => {
      const templatePage = getPageByOptionId(template, PAGE_OPTION.TOC);
      addPages([generatePage(templatePage)], {
        index: template.cover ? 1 : 0,
      });
    },
    isDisabled: ({ enabledTOC }) => enabledTOC,
  },

  //
  // Add Personal Timelines
  // Choose N people first.
  // Returns timeline pages for each selected person.
  //
  'personal-timeline': {
    title: 'Add Timeline Page',
    text:
      'Add a Timeline page for a single new person from your tree.  This page will also list significant world events below the timeline.',
    onSelect: ({ addPages, card, openPanel }) => {
      openPanel({
        props: { onAddPages: addPages },
        renderPanel: PersonalTimelinePanel,
        title: card.title,
      });
    },
    isDisabled: () => false,
  },

  //
  // Add Family Group Sheet
  // Choose 1 couple first.
  // Returns one group sheet page for selected couple.
  //
  'family-sheet': {
    title: 'Add Family Group Sheet',
    text:
      'A Family Group Sheet has the names of two parents and their children. They include birth, marriage and death information for everyone in the family.',
    onSelect: ({ addPages, card, openPanel }) => {
      openPanel({
        props: { onAddPages: addPages },
        renderPanel: FamilySheetPanel,
        title: card.title,
      });
    },
    isDisabled: () => false,
  },

  //
  // Add Records
  // Choose N people first.
  // Returns records pages for each selected person.
  //
  record: {
    title: 'Add Record Pages',
    text:
      'We do not automatically include records from Ancestry. To automatically add all available record pages for a person in this book, select them here.',
    onSelect: ({ addPages, card, openPanel }) => {
      openPanel({
        props: { onAddPages: addPages, openPanel },
        renderPanel: RecordPanel,
        title: card.title,
      });
    },
    isDisabled: ({ template }) => !getTemplatePage(template, 'record'),
  },

  //
  // Add Photos
  // Choose N people first.
  // Returns photos pages for each selected person.
  //
  photo: {
    title: 'Add Photo Pages',
    text:
      'We do not automatically include photos from Ancestry. To automatically add all available photos for a person in this book, select them here.',
    onSelect: ({ addPages, card, openPanel }) => {
      openPanel({
        props: { onAddPages: addPages, openPanel },
        renderPanel: PhotoPanel,
        title: card.title,
      });
    },
    isDisabled: ({ template }) => !getTemplatePage(template, 'blank'),
  },

  //
  // Add Family Tree
  // Choose N people first.
  // Returns whole book data for selected person or couple.
  //
  'family-tree': {
    title: 'Add a Family Tree',
    text:
      'To extend a book to include more generations, find the last person in the book with additional generations on Ancestry. Using this action, find and add them and the number of subsequent generations you want to include.',
    onSelect: ({ addPages, card, openPanel }) => {
      openPanel({
        props: { onAddPages: addPages },
        renderPanel: FamilyTreePanel,
        title: card.title,
      });
    },
    isDisabled: () => false,
  },

  //
  // Add Person
  // Choose person or couple first.
  // Returns everything for selected person or couple.
  //
  person: {
    title: 'Add a Person',
    text:
      'Add a missing person from your tree beyond the number of generations you included. You can optionally include their records and other related pages.',
    onSelect: ({ addPages, card, openPanel }) => {
      openPanel({
        props: { onAddPages: addPages },
        renderPanel: PersonPanel,
        title: card.title,
      });
    },
    isDisabled: () => false,
  },
};

export default OPTIONS;
